import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
    Bot,
    Wrench,
    Clock,
    BookOpen,
    Mic,
    Languages,
    Calendar,
    MessageSquare,
    AudioLines,
    Voicemail,
    PhoneOff,
    Phone,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { FadeId } from '@/components/ui/fade-id';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Assistant } from '@/api/assistants/assistants.types';

const itemVariants = {
    hidden: {
        opacity: 0,
        y: 10
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            duration: 0.3
        }
    }
};

export const AssistantSkeleton = () => (
    <Card className="shadow-sm">
        <div className="p-5 space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0 space-y-2">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-1/3" />
                    </div>
                </div>
                <Skeleton className="h-8 w-36 rounded-md" />
            </div>

            {/* Grid Skeleton */}
            <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="h-11 flex items-center justify-center">
                        <Skeleton className="h-6 w-16" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <Skeleton className="h-6 w-12" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <Skeleton className="h-6 w-8" />
                    </div>
                </div>
                <div className="border-t border-border" />
                <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="h-11 flex items-center justify-center">
                        <Skeleton className="h-6 w-12" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <Skeleton className="h-6 w-14" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <Skeleton className="h-6 w-16" />
                    </div>
                </div>
            </div>
        </div>
    </Card>
);

interface StatButtonProps {
    icon: React.ElementType;
    value: string | number;
    label: string;
    tooltip: string;
    active?: boolean;
}

const StatButton = ({ icon: Icon, value, label, tooltip, active = false }: StatButtonProps) => {
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            "h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2",
                            "hover:bg-muted/50 transition-colors cursor-default",
                            active && "bg-primary/5"
                        )}

                    >
                        <div className="flex items-center gap-1 mb-0.5">
                            <Icon className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-semibold">{value}</span>
                        </div>
                        <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="text-xs">{tooltip}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export const AssistantCard = ({ assistant }: { assistant: Assistant }) => {
    const navigate = useNavigate();

    // Calculate stats
    const toolsCount = assistant.model?.toolIds?.length || 0;
    const maxDuration = assistant.maxDurationSeconds ? `${Math.round(assistant.maxDurationSeconds / 60)}m` : '10m';
    const hasKnowledge = assistant.model?.knowledgeBaseId ? 'Yes' : 'No';

    // Get active features for badges
    const getActiveFeatures = () => {
        const features = [];

        if (assistant.firstMessageMode === 'assistant-speaks-first') {
            features.push({ icon: MessageSquare, label: "Speaks First" });
        }

        if (assistant.firstMessageMode === 'assistant-waits-for-user') {
            features.push({ icon: MessageSquare, label: "Waits" });
        }

        if (assistant.artifactPlan?.recordingEnabled) {
            features.push({ icon: AudioLines, label: "Recording" });
        }

        if (assistant.voicemailDetection?.enabled) {
            features.push({ icon: Voicemail, label: "Voicemail" });
        }

        if (assistant.endCallFunctionEnabled) {
            features.push({ icon: PhoneOff, label: "End Call" });
        }

        if (assistant.dialKeypadFunctionEnabled) {
            features.push({ icon: Phone, label: "Keypad" });
        }

        return features;
    };

    return (
        <motion.div variants={itemVariants}>
            <Card
                className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
                onClick={() => navigate(`/dashboard/assistants/${assistant.id}`)}
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title, and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm">
                                    <Bot className="h-5 w-5 text-primary" />
                                </div>
                            </div>

                            {/* Title and Date */}
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-semibold tracking-tight truncate">
                                    {assistant.name}
                                </h3>
                                {/* Date */}
                                {assistant.updatedAt && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(assistant.updatedAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId 
                                id={assistant.id} 
                                label="Assistant ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Unified Configuration Grid with Dividers */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Primary Config Row - Voice, Language, Tools */}
                            {assistant.voice?.voiceId && (
                                <StatButton
                                    icon={Mic}
                                    value={assistant.voice.voiceId}
                                    label="Voice"
                                    tooltip={`Voice: ${assistant.voice.voiceId}`}
                                />
                            )}
                            
                            {assistant.transcriber?.language && (
                                <StatButton
                                    icon={Languages}
                                    value={assistant.transcriber.language.toUpperCase()}
                                    label="Language"
                                    tooltip={`Language: ${assistant.transcriber.language}`}
                                />
                            )}

                            <StatButton
                                icon={Wrench}
                                value={toolsCount}
                                label="Tools"
                                tooltip={`${toolsCount} tool${toolsCount === 1 ? '' : 's'} connected`}
                                active={toolsCount > 0}
                            />
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div className="border-t border-border" />
                        
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Secondary Config Row - Duration, Knowledge, Mode */}
                            <StatButton
                                icon={Clock}
                                value={maxDuration}
                                label="Duration"
                                tooltip={`Maximum call duration: ${maxDuration}`}
                            />
                            <StatButton
                                icon={BookOpen}
                                value={hasKnowledge}
                                label="Knowledge"
                                tooltip={hasKnowledge === 'Yes' ? 'Knowledge base enabled' : 'No knowledge base'}
                                active={hasKnowledge === 'Yes'}
                            />

                            {/* Mode - Always show the first message mode */}
                            {assistant.firstMessageMode === 'assistant-speaks-first' ? (
                                <StatButton
                                    icon={MessageSquare}
                                    value="Speaks"
                                    label="Mode"
                                    tooltip="Assistant speaks first"
                                />
                            ) : assistant.firstMessageMode === 'assistant-speaks-first-with-model-generated-message' ? (
                                <StatButton
                                    icon={MessageSquare}
                                    value="AI First"
                                    label="Mode"
                                    tooltip="Assistant speaks first with model-generated message"
                                />
                            ) : assistant.firstMessageMode === 'assistant-waits-for-user' ? (
                                <StatButton
                                    icon={MessageSquare}
                                    value="Waits"
                                    label="Mode"
                                    tooltip="Assistant waits for user"
                                />
                            ) : (
                                <StatButton
                                    icon={MessageSquare}
                                    value="Default"
                                    label="Mode"
                                    tooltip="Default message mode"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};
